import { ChevronDown, ChevronUp, GripVertical } from "lucide-react";
import { useRef, useState } from "react";
import { Column, SortDirection } from "./types";

interface TableColumnHeaderProps<T extends Record<string, any>> {
  column: Column<T>;
  sortColumn: Extract<keyof T, string> | null;
  sortDirection: SortDirection;
  columnFilters: Partial<Record<Extract<keyof T, string>, string>>;
  onSort: (columnKey: Extract<keyof T, string>) => void;
  onFilter: (columnKey: Extract<keyof T, string>, value: string) => void;
  variant?: 'default' | 'soft';
  /** Réordonnancement par drag & drop : glisser cet en-tête sur un autre. */
  reorderable?: boolean;
  onColumnDrop?: (fromKey: string, toKey: string) => void;
}

const DRAG_MIME = 'application/x-table-column';

const TableColumnHeader = <T extends Record<string, any>>({
  column,
  sortColumn,
  sortDirection,
  columnFilters,
  onSort,
  onFilter,
  variant = 'default',
  reorderable = false,
  onColumnDrop
}: TableColumnHeaderProps<T>) => {
  const isSoft = variant === 'soft';

  // État du drag & drop de colonnes (indicateur de drop + colonne tirée grisée)
  const [isDragging, setIsDragging] = useState(false);
  const [isDropTarget, setIsDropTarget] = useState(false);
  // Compteur enter/leave : évite le clignotement quand le curseur passe sur les enfants
  const dragDepth = useRef(0);

  const labelClass = isSoft
    ? 'text-[11px] font-semibold uppercase tracking-wider text-primary/80'
    : 'text-xs font-medium text-white tracking-wider';
  const hoverClass = isSoft ? 'hover:text-primary' : 'hover:bg-white/10';
  const caretActive = isSoft ? 'text-primary' : 'text-white';
  // Inactifs plus estompés pour que la direction active ressorte
  const caretInactive = isSoft ? 'text-primary/25' : 'text-white/40';
  // Le label (flex) s'aligne comme les cellules, quel que soit le variant
  const justify = column.align === 'right'
    ? 'justify-end'
    : column.align === 'center'
      ? 'justify-center'
      : '';

  // Tri actif sur cette colonne : surbrillance + libellé en gras + chevron marqué
  const isSortedAsc = sortColumn === column.key && sortDirection === 'asc';
  const isSortedDesc = sortColumn === column.key && sortDirection === 'desc';
  const isSorted = isSortedAsc || isSortedDesc;
  const sortedBg = isSorted ? (isSoft ? 'bg-primary/10' : 'bg-white/15') : '';
  const caretPill = isSoft ? 'bg-primary/15' : 'bg-white/25';

  const isValidDrag = (e: React.DragEvent) => e.dataTransfer.types.includes(DRAG_MIME);
  const resetDrop = () => {
    dragDepth.current = 0;
    setIsDropTarget(false);
  };

  // Surbrillance de la cible de drop (fond léger selon le variant)
  const dropTargetBg = isDropTarget ? (isSoft ? 'bg-primary/10' : 'bg-white/10') : '';

  return (
    <th
      className={`group relative px-6 py-3 text-${column.align || 'left'} ${labelClass} ${dropTargetBg || sortedBg} ${
        column.sortable ? `cursor-pointer ${hoverClass}` : ''
      } ${reorderable ? 'select-none' : ''} ${isDragging ? 'opacity-40' : ''} ${
        column.width ? `w-${column.width}` : ''
      } ${column.className || ''}`}
      onClick={() => column.sortable && onSort(column.key)}
      // Drag & drop de colonnes : glisser un en-tête sur un autre le place avant celui-ci
      draggable={reorderable}
      title={reorderable ? 'Glisser pour réordonner les colonnes' : undefined}
      onDragStart={
        reorderable
          ? (e) => {
              e.dataTransfer.setData(DRAG_MIME, column.key);
              e.dataTransfer.effectAllowed = 'move';
              setIsDragging(true);
            }
          : undefined
      }
      onDragEnd={reorderable ? () => { setIsDragging(false); resetDrop(); } : undefined}
      onDragEnter={
        reorderable
          ? (e) => {
              // Pas d'indicateur sur la colonne en cours de déplacement
              if (isValidDrag(e) && !isDragging) {
                dragDepth.current += 1;
                setIsDropTarget(true);
              }
            }
          : undefined
      }
      onDragOver={
        reorderable
          ? (e) => {
              if (isValidDrag(e)) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }
            }
          : undefined
      }
      onDragLeave={
        reorderable
          ? (e) => {
              if (isValidDrag(e)) {
                dragDepth.current -= 1;
                if (dragDepth.current <= 0) setIsDropTarget(false);
              }
            }
          : undefined
      }
      onDrop={
        reorderable
          ? (e) => {
              e.preventDefault();
              resetDrop();
              const fromKey = e.dataTransfer.getData(DRAG_MIME);
              if (fromKey && fromKey !== column.key) {
                onColumnDrop?.(fromKey, column.key);
              }
            }
          : undefined
      }
    >
      {/* Indicateur de drop : barre verticale à gauche = insertion avant cette colonne */}
      {isDropTarget && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 w-1 rounded-full bg-secondary shadow-[0_0_6px_1px_var(--secondary-color)]"
        />
      )}
      {/* Poignée en absolu (hors flux) pour ne pas décaler le centrage du label */}
      {reorderable && (
        <GripVertical
          size={13}
          aria-hidden
          className={`pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 opacity-0 transition-opacity group-hover:opacity-60 ${
            isSoft ? 'text-primary/60' : 'text-white/70'
          }`}
        />
      )}
      <div className={`flex items-center gap-2 ${justify}`}>
        <span className={isSorted ? 'font-bold' : ''}>{column.label}</span>
        {column.sortable &&
          (isSorted ? (
            // Tri actif : un seul chevron bien marqué dans une pastille contrastée
            <span
              className={`inline-flex items-center justify-center rounded-md p-0.5 ${caretPill}`}
            >
              {isSortedAsc ? (
                <ChevronUp size={15} strokeWidth={3} className={caretActive} />
              ) : (
                <ChevronDown size={15} strokeWidth={3} className={caretActive} />
              )}
            </span>
          ) : (
            // Colonne triable non triée : affordance discrète (double chevron estompé)
            <div className="flex flex-col">
              <ChevronUp size={14} className={caretInactive} />
              <ChevronDown size={14} className={caretInactive} />
            </div>
          ))}
      </div>
      {column.filterable && (
        <input
          type="text"
          placeholder="Filtrer..."
          value={columnFilters[column.key] || ''}
          onChange={(e) => onFilter(column.key, e.target.value)}
          className="mt-2 w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary"
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </th>
  );
};

export default TableColumnHeader
